import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { SITE, NAV } from "./content";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <header
      className={`vw-header fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "vw-header--scrolled" : ""
      }`}
      data-testid="site-header"
    >
      <div className="vw-container flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img
            src={SITE.logo}
            alt="Verified Workforce"
            className="h-11 w-auto"
            data-testid="header-logo-img"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `text-[13px] font-semibold tracking-wide transition-colors ${
                  isActive ? "text-[var(--vw-blue)]" : "text-[#0B1120] hover:text-[var(--vw-blue)]"
                }`
              }
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            className="hidden xl:inline-flex vw-btn vw-btn--sm vw-btn--outline-dark"
            data-testid="header-find-contractors-btn"
          >
            Find Contractors
          </Link>
          <Link
            to="/delivery-partners#apply"
            className="vw-btn vw-btn--sm vw-btn--primary"
            data-testid="header-become-partner-btn"
          >
            Become a Verified Partner
            <ArrowUpRight size={14} />
          </Link>
        </div>

        <button
          className="xl:hidden p-2 text-[#0A0F24]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden border-t border-black/10 bg-[#FAFAF7]"
            data-testid="mobile-menu"
          >
            <div className="vw-container py-6 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `py-3 border-b border-black/5 text-base font-semibold ${
                      isActive ? "text-[var(--vw-blue)]" : "text-[#0A0F24]"
                    }`
                  }
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="mt-5 flex items-center justify-between gap-3">
                <a
                  href={`tel:${SITE.contact.phoneRaw}`}
                  className="flex items-center gap-2 text-sm font-semibold text-[#0A0F24]"
                  data-testid="mobile-header-phone"
                >
                  <Phone size={14} className="text-[var(--vw-gold)]" />
                  {SITE.contact.phone}
                </a>
                <Link to="/delivery-partners#apply" className="vw-btn vw-btn--sm vw-btn--primary" data-testid="mobile-cta-btn">
                  Become a Verified Partner
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
